// ============================================================================
// PRELIT-CLOSED-REVIEW-001 — Supervisor Review of Closed Early-Recovery Cases
// ============================================================================
// Optional supervisor worksheet (Resources/info 2.txt §2). Lists closed
// PRE_LITIGATION cases. Tabs partition by review_status. Row actions:
//   - Confirm Review → sets review_status = CONFIRMED (auditable trail)
//   - Reopen         → reactivates the workflow with a required reason
//   - Open           → opens the standard case detail modal
//
// Visibility is server-side: only supervisor roles get review_closure + reopen_case.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import { useClosedCases } from 'hooks/api/usePreLitigation';
import type {
  ClosedCaseReviewRow,
  ClosedCasesReviewSearchRequest,
} from 'api/types/preLitigation.types';
import { openCaseDetail } from 'components/modules/pre-litigation-module/openCaseDetail';
import ConfirmClosureReviewModal from './ConfirmClosureReviewModal';
import ReopenPreLitCaseModal from './ReopenPreLitCaseModal';

interface Props {
  tabId?: string;
  moduleName?: string;
}

type ReviewTab = 'PENDING' | 'CONFIRMED' | 'ALL';

const REVIEW_TABS: { id: ReviewTab; label: string; icon: keyof typeof IconSystem }[] = [
  { id: 'PENDING',   label: 'Pending Review', icon: 'bell' },
  { id: 'CONFIRMED', label: 'Confirmed',      icon: 'checkCircle' },
  { id: 'ALL',       label: 'All',            icon: 'list' },
];

const OUTCOME_OPTIONS = [
  { value: '',              label: 'Any Outcome' },
  { value: 'FAVOURABLE',    label: 'Favourable (Won)' },
  { value: 'UNFAVOURABLE',  label: 'Unfavourable (Lost)' },
  { value: 'NEUTRAL',       label: 'Neutral' },
];

const formatDate = (v?: string) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return v;
  }
};

const ClosedCasesReviewScreen: React.FC<Props> = ({ tabId }) => {
  const [activeTab, setActiveTab] = useState<ReviewTab>('PENDING');
  const [queryParams, setQueryParams] = useState<ClosedCasesReviewSearchRequest>({
    page: 1,
    pageSize: 25,
    reviewStatus: 'PENDING',
  });
  const { data, loading, pagination, refetch } = useClosedCases(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter panel state
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [closedFrom, setClosedFrom]       = useState('');
  const [closedTo, setClosedTo]           = useState('');

  // Modals
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [reopenModalOpen, setReopenModalOpen]   = useState(false);
  const [selectedRow, setSelectedRow]           = useState<ClosedCaseReviewRow | null>(null);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleTabChange = useCallback((tab: ReviewTab) => {
    setActiveTab(tab);
    setQueryParams(prev => ({ ...prev, reviewStatus: tab, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      outcomeCategory: outcomeFilter || undefined,
      closedFrom: closedFrom || undefined,
      closedTo:   closedTo   || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [outcomeFilter, closedFrom, closedTo]);

  const clearFilters = useCallback(() => {
    setOutcomeFilter(''); setClosedFrom(''); setClosedTo('');
    setQueryParams(prev => ({
      page: 1,
      pageSize: prev.pageSize ?? 25,
      reviewStatus: prev.reviewStatus,
    }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.outcomeCategory) n++;
    if (queryParams.closedFrom)      n++;
    if (queryParams.closedTo)        n++;
    return n;
  }, [queryParams]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // Grid columns
  const columns: SyutGridColumn<ClosedCaseReviewRow>[] = useMemo(() => [
    { id: 'caseCode',      header: 'Case #',   accessorKey: 'caseCode',      size: 120, enableSorting: true },
    { id: 'accountNumber', header: 'Account #', accessorKey: 'accountNumber', size: 130, enableSorting: true },
    { id: 'customerName',  header: 'Customer', accessorKey: 'customerName',  size: 180, enableSorting: true, flex: true },
    {
      id: 'branchCode', header: 'Branch',
      accessorKey: 'branchCode', size: 90,
      cell: ({ row }) => row.original.branchCode ?? '—',
    },
    {
      id: 'closureReasonName', header: 'Closure Reason',
      accessorKey: 'closureReasonName', size: 180, enableSorting: true,
      cell: ({ row }) => row.original.closureReasonName ?? '—',
    },
    {
      id: 'outcomeCategory', header: 'Outcome',
      accessorKey: 'outcomeCategory', size: 130, enableSorting: true,
      cell: ({ row }) => {
        const cat = row.original.outcomeCategory;
        const color = cat === 'FAVOURABLE' ? '#16a34a' : cat === 'UNFAVOURABLE' ? '#dc2626' : '#6b7280';
        const label = cat === 'FAVOURABLE' ? 'Won' : cat === 'UNFAVOURABLE' ? 'Lost' : 'Neutral';
        return (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600,
            padding: '2px 8px', borderRadius: '999px',
            color, border: `1px solid ${color}`,
          }}>{label}</span>
        );
      },
    },
    {
      id: 'recoveredAmount', header: 'Recovered ₹',
      accessorKey: 'recoveredAmount', size: 120, enableSorting: true,
      cell: ({ row }) => row.original.recoveredAmount != null
        ? `₹${row.original.recoveredAmount.toLocaleString('en-IN')}` : '—',
    },
    {
      id: 'closedAt', header: 'Closed At',
      accessorKey: 'closedAt', size: 120, enableSorting: true,
      cell: ({ row }) => formatDate(row.original.closedAt),
    },
    {
      id: 'closedByName', header: 'Closed By',
      accessorKey: 'closedByName', size: 150,
      cell: ({ row }) => row.original.closedByName ?? '—',
    },
    {
      id: 'reviewStatus', header: 'Review',
      accessorKey: 'reviewStatus', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const s = row.original.reviewStatus;
        const color = s === 'CONFIRMED' ? '#16a34a' : '#d97706';
        return (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600,
            padding: '2px 8px', borderRadius: '999px',
            color, border: `1px solid ${color}`,
          }}>{s === 'CONFIRMED' ? 'Confirmed' : 'Pending'}</span>
        );
      },
    },
    {
      id: 'reviewedByName', header: 'Reviewed By',
      accessorKey: 'reviewedByName', size: 150,
      cell: ({ row }) => row.original.reviewedByName ?? '—',
    },
  ], []);

  // Row actions
  const getRowActions = useCallback((row: ClosedCaseReviewRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'open',
        label: 'Open Case',
        icon: <IconSystem.eye size={16} />,
        onClick: () => openCaseDetail(String(row.id)),
      },
    ];
    if (row.reviewStatus === 'PENDING') {
      items.push({
        id: 'confirm',
        label: 'Confirm Review',
        icon: <IconSystem.checkCircle size={16} />,
        onClick: () => { setSelectedRow(row); setConfirmModalOpen(true); },
      });
    }
    items.push({
      id: 'reopen',
      label: 'Reopen Case',
      icon: <IconSystem.refresh size={16} />,
      onClick: () => { setSelectedRow(row); setReopenModalOpen(true); },
      variant: 'danger',
    });
    return items;
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Reload closed cases',
      onClick: refetch,
      variant: 'icon',
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Closed Cases Review"
        description="Supervisor worksheet — review, confirm, or reopen closed early-recovery cases."
        icon={<IconSystem.checkCircle size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search account, customer, case #...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      {/* Status tabs — directly below ModuleHeader */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        padding: '0.5rem 1.25rem 0',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        flexShrink: 0,
        overflowX: 'auto',
      }}>
        {REVIEW_TABS.map((tab) => {
          const Icon = (IconSystem as any)[tab.icon] ?? IconSystem.list;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.9rem',
                border: 'none',
                background: 'transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 500,
                borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              frozenRightColumns={['actions']}
              emptyMessage={loading ? 'Loading...' : 'No closed cases in this tab.'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Outcome"
                  options={OUTCOME_OPTIONS}
                  value={outcomeFilter}
                  onChange={(val) => setOutcomeFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Closed From</label>
                <input type="date" className="form-input" value={closedFrom} onChange={(e) => setClosedFrom(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Closed To</label>
                <input type="date" className="form-input" value={closedTo} onChange={(e) => setClosedTo(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmClosureReviewModal
        isOpen={confirmModalOpen}
        row={selectedRow}
        onClose={() => setConfirmModalOpen(false)}
        onSuccess={refetch}
      />

      <ReopenPreLitCaseModal
        isOpen={reopenModalOpen}
        row={selectedRow}
        onClose={() => setReopenModalOpen(false)}
        onSuccess={refetch}
      />
    </div>
  );
};

export default ClosedCasesReviewScreen;
